import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ConsentModal } from "@/components/ConsentModal";
import {
  LayoutDashboard,
  ScanLine,
  ListChecks,
  FileText,
  Sparkles,
  Zap,
  Receipt,
  Home,
  Scale,
  ShieldAlert,
  LogOut,
  Menu,
  X,
  Crown,
  CreditCard,
  Mail,
  BookOpen,
  ShieldCheck,
  Download,
  FileSearch,
  Gift,
  Settings,
} from "lucide-react";
import { useState } from "react";

const items = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/app/scan", label: "AI Scan", icon: ScanLine },
  { to: "/app/get-report", label: "Get My Report", icon: Download },
  { to: "/app/report", label: "Review Report", icon: FileSearch },
  { to: "/app/items", label: "Negative Items", icon: ListChecks },
  { to: "/app/disputes", label: "Disputes", icon: FileText },
  { to: "/app/mail", label: "Certified Mail", icon: Mail, paid: true },
  { to: "/app/guide", label: "Credit Playbook", icon: BookOpen },
  { to: "/app/refer", label: "Invite Friends", icon: Gift },
  { to: "/app/tips", label: "Coach", icon: Sparkles },
];

const plusItems = [
  { to: "/app/mortgage-sim", label: "Mortgage Sim", icon: Home },
  { to: "/app/cfpb", label: "CFPB Escalation", icon: Scale },
  { to: "/app/idtheft", label: "ID-Theft Kit", icon: ShieldAlert },
];

const billingItems = [
  { to: "/app/pricing", label: "Upgrade", icon: Zap },
  { to: "/app/billing", label: "Billing", icon: Receipt },
];

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[#F5F3EE] flex flex-col">
      <header className="border-b border-[#26282C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-brand">
            <div className="w-7 h-7 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
              <span className="font-display italic text-[#0B0C0E] text-base leading-none">a</span>
            </div>
            <span className="font-display text-xl">Aura Credit</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-mono uppercase tracking-wider text-[#A8ADA5]">
            <Link to="/playbook" className="hover:text-[#D5FF3F]">Playbook</Link>
            <Link to="/terms" className="hover:text-[#D5FF3F]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#D5FF3F]">Privacy</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button className="rounded-full btn-outline h-9 px-4 text-sm">Sign in</Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full btn-lime h-9 px-4 text-sm">Get started</Button>
            </Link>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">{children}</div>
      </main>
      <footer className="border-t border-[#26282C] bg-[#0B0C0E]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-xs text-[#6D716C] font-mono uppercase tracking-wider">
            © {new Date().getFullYear()} · No human ever reads your file
          </div>
          <div className="flex flex-wrap gap-5 text-xs font-mono uppercase tracking-wider text-[#6D716C]">
            <Link to="/playbook" className="hover:text-[#D5FF3F]">Playbook</Link>
            <Link to="/terms" className="hover:text-[#D5FF3F]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#D5FF3F]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Chooses AppShell for logged-in users, PublicShell for logged-out. Used by SEO pages.
 */
export function AutoShell({ children }) {
  const { user } = useAuth();
  return user ? <AppShell>{children}</AppShell> : <PublicShell>{children}</PublicShell>;
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[#F5F3EE] flex">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 border-r border-[#26282C] bg-[#0B0C0E] transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col p-6">
          <Link to="/app" className="flex items-center gap-2.5 mb-10" onClick={() => setOpen(false)}>
            <div className="w-8 h-8 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
              <span className="font-display italic text-[#0B0C0E] text-lg leading-none">a</span>
            </div>
            <div>
              <div className="font-display text-xl leading-none">Aura Credit</div>
              <div className="overline mt-1.5">v1.0 · alpha</div>
            </div>
          </Link>

          <nav className="flex-1 space-y-1 overflow-y-auto">
            {items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              const paidUser = user?.plan === "basic" || user?.plan === "plus";
              const showPaidTag = item.paid && !paidUser && !active;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    active
                      ? "bg-[#D5FF3F] text-[#0B0C0E] font-medium"
                      : "text-[#A8ADA5] hover:bg-[#17191C] hover:text-[#F5F3EE]"
                  }`}
                >
                  <Icon size={16} strokeWidth={1.75} />
                  <span className="flex-1">{item.label}</span>
                  {showPaidTag && (
                    <span className="text-[9px] uppercase tracking-widest text-[#D5FF3F]/70 font-mono">Paid</span>
                  )}
                </Link>
              );
            })}

            <div className="pt-4 pb-1 px-3 flex items-center gap-2">
              <Crown size={11} className="text-[#D5FF3F]" />
              <span className="text-[10px] uppercase tracking-widest text-[#6D716C] font-mono">
                Plus tools
              </span>
            </div>
            {plusItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              const isPlusUser = user?.plan === "plus";
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    active
                      ? "bg-[#D5FF3F] text-[#0B0C0E] font-medium"
                      : "text-[#A8ADA5] hover:bg-[#17191C] hover:text-[#F5F3EE]"
                  }`}
                >
                  <Icon size={16} strokeWidth={1.75} />
                  <span className="flex-1">{item.label}</span>
                  {!isPlusUser && !active && (
                    <span className="text-[9px] uppercase tracking-widest text-[#D5FF3F]/70 font-mono">Plus</span>
                  )}
                </Link>
              );
            })}

            <div className="pt-4" />
            {billingItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    active
                      ? "bg-[#D5FF3F] text-[#0B0C0E] font-medium"
                      : "text-[#A8ADA5] hover:bg-[#17191C] hover:text-[#F5F3EE]"
                  }`}
                >
                  <Icon size={16} strokeWidth={1.75} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {user?.is_admin && (
              <>
                <div className="pt-4 pb-1 px-3 flex items-center gap-2">
                  <Settings size={11} className="text-[#FF6B4A]" />
                  <span className="text-[10px] uppercase tracking-widest text-[#6D716C] font-mono">
                    Admin
                  </span>
                </div>
                <Link
                  to="/app/admin/tradelines"
                  data-testid="nav-tradelines-admin"
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    location.pathname.startsWith("/app/admin/tradelines")
                      ? "bg-[#D5FF3F] text-[#0B0C0E] font-medium"
                      : "text-[#A8ADA5] hover:bg-[#17191C] hover:text-[#F5F3EE]"
                  }`}
                >
                  <CreditCard size={16} strokeWidth={1.75} />
                  <span>Tradelines Admin</span>
                </Link>
              </>
            )}
          </nav>

          <div className="border-t border-[#26282C] pt-4 mt-4">
            <div className="text-sm mb-3">
              <div className="font-medium truncate">{user?.name || "Member"}</div>
              <div className="text-[#6D716C] text-xs truncate font-mono">{user?.email}</div>
            </div>
            <div className="flex items-start gap-1.5 mb-3 px-1">
              <ShieldCheck size={11} className="text-[#D5FF3F] flex-shrink-0 mt-0.5" />
              <Link
                to="/app/disclaimer"
                data-testid="nav-disclaimer-link"
                className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] hover:text-[#D5FF3F] leading-tight"
              >
                No human sees your data.<br />
                <span className="text-[#A8ADA5]">Privacy &amp; disclaimer →</span>
              </Link>
            </div>
            <Button
              data-testid="logout-btn"
              variant="outline"
              className="w-full rounded-sm btn-outline"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
            >
              <LogOut size={15} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-20 bg-[#0B0C0E] border-b border-[#26282C] px-4 py-3 flex items-center justify-between">
          <button
            data-testid="mobile-nav-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 rounded-sm hover:bg-[#17191C]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="font-display text-lg">Aura Credit</div>
          <div className="w-9" />
        </div>
        <div className="p-6 lg:p-10 max-w-[1400px]">{children}</div>
      </main>
      <ConsentModal />
    </div>
  );
}
